import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import {
  Calculator, Upload, FileSpreadsheet, RefreshCw, Search,
  AlertTriangle, Banknote, TrendingUp, Receipt, Trash2, Wand2, Wallet,
  Truck, BadgeCheck, Download, Link2, Filter, X, ChevronLeft, ChevronRight,
  Package, FileText, CheckCircle2,
} from "lucide-react";
import {
  getAccountingKpis, listAccountingOrders, getAccountingOrder,
  backfillInvoiceLinks, downloadExport,
  listExceptions, listImports,
  uploadGlsXlsx, deleteImport, recomputeImport, reconcileAll, markBatchPaid,
  uploadSmartBillXlsx, listSmartBillImports,
  reclassifyStorno, listSmartBillUnmatched, searchShopifyOrders,
  linkInvoiceToOrder, downloadUnmatchedExport,
} from "@/lib/accounting";
import { useRealtimeEvent } from "@/lib/realtime-context";
import { useAuth } from "@/lib/auth";
import { fmtDate, fmtRelative } from "@/lib/format";

const ron = (n, c = "RON") => `${Number(n || 0).toLocaleString("ro-RO", { maximumFractionDigits: 2 })} ${c}`;

// ----- Status labels (must mirror backend orders_view.py enums) -----
const DELIVERY_LABEL = {
  new:               { t: "Nouă",                       cls: "text-zinc-700 bg-zinc-100 border-zinc-200" },
  pending:           { t: "Așteaptă preluare GLS",      cls: "text-sky-700 bg-sky-50 border-sky-200" },
  in_transit:        { t: "În tranzit",                 cls: "text-blue-700 bg-blue-50 border-blue-200" },
  out_for_delivery:  { t: "În livrare azi",             cls: "text-indigo-700 bg-indigo-50 border-indigo-200" },
  delivery_failed:   { t: "Tentativă eșuată",           cls: "text-orange-700 bg-orange-50 border-orange-200" },
  at_pickup_point:   { t: "În ParcelShop",              cls: "text-violet-700 bg-violet-50 border-violet-200" },
  delivered:         { t: "Livrată",                    cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  refused:           { t: "Refuzată",                   cls: "text-rose-700 bg-rose-50 border-rose-200" },
  return:            { t: "Retur",                      cls: "text-amber-700 bg-amber-50 border-amber-200" },
  lost:              { t: "Pierdută",                   cls: "text-rose-700 bg-rose-50 border-rose-200" },
};
const INVOICE_LABEL = {
  none:      { t: "Neemisă",   cls: "text-zinc-700 bg-zinc-100 border-zinc-200" },
  issued:    { t: "Emisă",     cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  cancelled: { t: "Anulată",   cls: "text-rose-700 bg-rose-50 border-rose-200" },
  storno:    { t: "Stornată",  cls: "text-amber-700 bg-amber-50 border-amber-200" },
};
const COLLECTION_LABEL = {
  unpaid:        { t: "Neîncasată",         cls: "text-zinc-700 bg-zinc-100 border-zinc-200" },
  awaiting_gls:  { t: "Așteaptă decont GLS",cls: "text-amber-700 bg-amber-50 border-amber-200" },
  partial:       { t: "Parțială",           cls: "text-amber-700 bg-amber-50 border-amber-200" },
  paid:          { t: "Decontată",          cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  amount_diff:   { t: "Dif. sumă",          cls: "text-amber-700 bg-amber-50 border-amber-200" },
  exception:     { t: "Excepție",           cls: "text-rose-700 bg-rose-50 border-rose-200" },
};

const PERIOD_PRESETS = [
  { id: "today",      label: "Azi" },
  { id: "yesterday",  label: "Ieri" },
  { id: "last_7d",    label: "Ultimele 7z" },
  { id: "last_30d",   label: "Ultimele 30z" },
  { id: "last_90d",   label: "Ultimele 90z" },
  { id: "this_month", label: "Luna curentă" },
  { id: "last_month", label: "Luna trecută" },
  { id: "all",        label: "Tot istoricul" },
];

export default function AccountingPage() {
  const { hasPermission } = useAuth();
  const canView = hasPermission("accounting.view");
  const canImport = hasPermission("accounting.import");
  const canReconcile = hasPermission("accounting.reconcile");
  const canMarkPaid = hasPermission("accounting.mark_paid");
  const canExport = hasPermission("accounting.export");
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "orders";

  if (!canView) {
    return (
      <div className="p-8 max-w-2xl mx-auto" data-testid="accounting-forbidden">
        <Card className="p-8 text-center space-y-3">
          <Calculator className="h-12 w-12 mx-auto text-zinc-400" />
          <h2 className="font-display text-2xl font-bold">Fără acces</h2>
          <p className="text-muted-foreground text-sm">Nu ai permisiunea <code className="font-mono text-xs">accounting.view</code>.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="accounting-page">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 flex items-center gap-2">
            <Calculator className="h-3 w-3" /> Hub financiar · centru comenzi
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Contabilitate</h1>
          <p className="text-muted-foreground text-sm mt-1">
            1 rând = 1 comandă Shopify. Aici se leagă AWB GLS, factura SmartBill, decontul COD și încasarea.
          </p>
        </div>
      </header>

      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v })}>
        <TabsList className="bg-secondary/40">
          <TabsTrigger value="dashboard"  data-testid="acc-tab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="orders"     data-testid="acc-tab-orders">Comenzi</TabsTrigger>
          <TabsTrigger value="exceptions" data-testid="acc-tab-exceptions">Excepții</TabsTrigger>
          <TabsTrigger value="imports"    data-testid="acc-tab-imports">Import GLS</TabsTrigger>
          <TabsTrigger value="unmatched"  data-testid="acc-tab-unmatched">Necorelate</TabsTrigger>
          <TabsTrigger value="export"     data-testid="acc-tab-export">Export</TabsTrigger>
        </TabsList>
        <TabsContent value="dashboard"  className="mt-6"><DashboardTab /></TabsContent>
        <TabsContent value="orders"     className="mt-6"><OrdersTab canReconcile={canReconcile} canMarkPaid={canMarkPaid} /></TabsContent>
        <TabsContent value="exceptions" className="mt-6"><ExceptionsTab /></TabsContent>
        <TabsContent value="imports"    className="mt-6"><ImportsTab canImport={canImport} canReconcile={canReconcile} canMarkPaid={canMarkPaid} /></TabsContent>
        <TabsContent value="unmatched"  className="mt-6"><UnmatchedTab canReconcile={canReconcile} canExport={canExport} /></TabsContent>
        <TabsContent value="export"     className="mt-6"><ExportTab canExport={canExport} /></TabsContent>
      </Tabs>
    </div>
  );
}

// ============== DASHBOARD (order-centric KPIs) ==============
function DashboardTab() {
  const [period, setPeriod] = useState("last_30d");
  const [k, setK] = useState({});
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    setLoading(true);
    getAccountingKpis({ period }).then(setK).catch(() => {}).finally(() => setLoading(false));
  }, [period]);
  useEffect(() => { load(); }, [load]);
  useRealtimeEvent("accounting.batch_imported", load);
  useRealtimeEvent("invoice.generated", load);
  useRealtimeEvent("invoice.paid", load);

  const tiles = [
    { label: "Comenzi totale",      value: k.orders_total,      icon: Package,      money: false },
    { label: "Livrate",             value: k.orders_fulfilled,  icon: Truck,        money: false },
    { label: "Facturate",           value: k.orders_invoiced,   icon: Receipt,      money: false },
    { label: "Decontate",           value: k.orders_paid,       icon: Banknote,     money: false },
    { label: "Neîncasate",          value: k.orders_unpaid,     icon: Wallet,       money: false },
    { label: "Refuzate",            value: k.orders_refused,    icon: AlertTriangle,money: false },
    { label: "Valoare facturată",   value: k.value_invoiced,    icon: Receipt,      money: true  },
    { label: "Valoare decontată",   value: k.value_collected,   icon: TrendingUp,   money: true  },
    { label: "Valoare restantă",    value: k.value_outstanding, icon: Wallet,       money: true  },
    { label: "Cashflow estimat",    value: k.cashflow_estimate, icon: TrendingUp,   money: true  },
  ];
  return (
    <div className="space-y-4">
      <PeriodSelector value={period} onChange={setPeriod} />
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="acc-dashboard-grid">
        {loading ? Array.from({ length: 10 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />) :
          tiles.map((t) => (
            <div key={t.label} className="rounded-xl border border-border bg-white p-4">
              <div className="flex items-start justify-between">
                <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{t.label}</div>
                <t.icon className="h-4 w-4 text-zinc-500" />
              </div>
              <div className="mt-2 font-display text-2xl font-bold tabular-nums">
                {t.money ? ron(t.value) : Number(t.value || 0).toLocaleString("ro-RO")}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}

// ============== ORDERS (the new central table) ==============
function OrdersTab({ canReconcile, canMarkPaid }) {
  const [period, setPeriod] = useState("last_90d");
  const [filters, setFilters] = useState({});
  const [search, setSearch] = useState("");
  const [skip, setSkip] = useState(0);
  const [limit] = useState(50);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [openOrderId, setOpenOrderId] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [backfillBusy, setBackfillBusy] = useState(false);

  const queryParams = useMemo(() => {
    const p = { period, limit, skip, sort: "created_at" };
    if (search.trim()) p.search = search.trim();
    Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  }, [period, search, skip, limit, filters]);

  const load = useCallback(() => {
    setLoading(true);
    listAccountingOrders(queryParams)
      .then((r) => { setRows(r.items || []); setTotal(r.total || 0); })
      .catch(() => toast.error("Eroare la încărcarea comenzilor"))
      .finally(() => setLoading(false));
  }, [queryParams]);

  useEffect(() => {
    const t = setTimeout(load, search ? 320 : 0);
    return () => clearTimeout(t);
  }, [load, search]);
  useEffect(() => { setSkip(0); }, [period, filters, search]);

  useRealtimeEvent("accounting.batch_imported", load);
  useRealtimeEvent("invoice.generated", load);
  useRealtimeEvent("invoice.paid", load);

  const runBackfill = async () => {
    setBackfillBusy(true);
    try {
      const r = await backfillInvoiceLinks(true);
      toast.success(`Backfill: ${r.linked}/${r.scanned} facturi corelate (high=${r.confidence.high})`);
      load();
    } catch { toast.error("Eroare backfill"); }
    finally { setBackfillBusy(false); }
  };

  const reRunReconcile = async () => {
    try {
      const r = await reconcileAll();
      toast.success(`Recalculat ${r.items} item-uri GLS (${r.auto_matched} match-uri)`);
      load();
    } catch { toast.error("Eroare"); }
  };

  return (
    <div className="space-y-3">
      <PeriodSelector value={period} onChange={setPeriod} />

      <Card className="p-3 flex items-center gap-2 flex-wrap" data-testid="acc-orders-toolbar">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="#86872 / client / telefon / email…" className="pl-8 h-9" data-testid="acc-orders-search" />
        </div>
        <Button variant="outline" size="sm" onClick={() => setShowFilters((v) => !v)} data-testid="acc-orders-toggle-filters">
          <Filter className="h-3.5 w-3.5 mr-1" /> Filtre {Object.values(filters).filter(Boolean).length > 0 && <span className="ml-1 text-[10px] bg-primary text-white rounded-full px-1.5">{Object.values(filters).filter(Boolean).length}</span>}
        </Button>
        {canReconcile && (
          <>
            <Button variant="outline" size="sm" onClick={runBackfill} disabled={backfillBusy} data-testid="acc-orders-backfill-sb">
              <Link2 className="h-3.5 w-3.5 mr-1" /> {backfillBusy ? "..." : "Corelează facturi SB"}
            </Button>
            <Button variant="outline" size="sm" onClick={reRunReconcile} data-testid="acc-orders-recompute">
              <Wand2 className="h-3.5 w-3.5 mr-1" /> Recalculează GLS
            </Button>
          </>
        )}
      </Card>

      {showFilters && (
        <FiltersBar value={filters} onChange={setFilters} onClear={() => setFilters({})} />
      )}

      <Card className="overflow-x-auto" data-testid="acc-orders-table-card">
        <table className="w-full text-xs">
          <thead className="bg-secondary/40 border-b border-border sticky top-0 z-10">
            <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
              <th className="px-3 py-2 whitespace-nowrap">Comandă</th>
              <th className="px-3 py-2 whitespace-nowrap">Data</th>
              <th className="px-3 py-2 whitespace-nowrap">Client</th>
              <th className="px-3 py-2 whitespace-nowrap">Județ</th>
              <th className="px-3 py-2 text-right whitespace-nowrap">Total</th>
              <th className="px-3 py-2 whitespace-nowrap">Plată</th>
              <th className="px-3 py-2 whitespace-nowrap">AWB</th>
              <th className="px-3 py-2 whitespace-nowrap">Livrare</th>
              <th className="px-3 py-2 whitespace-nowrap">Factură</th>
              <th className="px-3 py-2 text-right whitespace-nowrap">Val. fact.</th>
              <th className="px-3 py-2 whitespace-nowrap">Status fact.</th>
              <th className="px-3 py-2 text-right whitespace-nowrap">Ramburs așt.</th>
              <th className="px-3 py-2 text-right whitespace-nowrap">Decontat</th>
              <th className="px-3 py-2 whitespace-nowrap">Status încasare</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? Array.from({ length: 12 }).map((_, i) => (
              <tr key={i}><td colSpan={14} className="px-3 py-2"><Skeleton className="h-4 w-full" /></td></tr>
            )) : rows.length === 0 ? (
              <tr><td colSpan={14} className="px-3 py-10 text-center text-muted-foreground">Nicio comandă în perioada selectată.</td></tr>
            ) : rows.map((r) => {
              const dl = DELIVERY_LABEL[r.delivery_status] || DELIVERY_LABEL.new;
              const il = INVOICE_LABEL[r.invoice_status] || INVOICE_LABEL.none;
              const cl = COLLECTION_LABEL[r.collection_status] || COLLECTION_LABEL.unpaid;
              return (
                <tr key={r.order_id} className="hover:bg-secondary/30 cursor-pointer" onClick={() => setOpenOrderId(r.order_id)} data-testid={`acc-order-row-${r.order_number || r.order_id}`}>
                  <td className="px-3 py-2 font-mono font-semibold">#{r.order_number || "—"}</td>
                  <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{r.created_at ? fmtDate(r.created_at) : "—"}</td>
                  <td className="px-3 py-2 max-w-[160px] truncate" title={r.customer_name}>{r.customer_name || "—"}</td>
                  <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{r.county || "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold">{ron(r.order_total, r.currency)}</td>
                  <td className="px-3 py-2 text-muted-foreground max-w-[120px] truncate" title={r.payment_method}>{r.payment_method || "—"}</td>
                  <td className="px-3 py-2 font-mono">{r.awb || "—"}</td>
                  <td className="px-3 py-2" title={r.delivery_last_event || r.delivery_status_raw || ""}><Badge variant="outline" className={`text-[10px] ${dl.cls}`}>{dl.t}</Badge></td>
                  <td className="px-3 py-2 font-mono whitespace-nowrap">{r.invoice_series && r.invoice_number ? `${r.invoice_series}${r.invoice_number}` : "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.invoice_total ? ron(r.invoice_total, r.currency) : "—"}</td>
                  <td className="px-3 py-2"><Badge variant="outline" className={`text-[10px] ${il.cls}`}>{il.t}</Badge></td>
                  <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{r.expected_cod ? ron(r.expected_cod, r.currency) : "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.collected_amount ? ron(r.collected_amount, r.currency) : "—"}</td>
                  <td className="px-3 py-2"><Badge variant="outline" className={`text-[10px] ${cl.cls}`}>{cl.t}</Badge></td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <Pagination total={total} skip={skip} limit={limit} onPrev={() => setSkip(Math.max(0, skip - limit))} onNext={() => setSkip(skip + limit)} />
      </Card>

      <OrderDetailSheet orderId={openOrderId} onClose={() => setOpenOrderId(null)} canMarkPaid={canMarkPaid} />
    </div>
  );
}

// --- Period selector ---
function PeriodSelector({ value, onChange }) {
  return (
    <div className="flex items-center gap-1 flex-wrap" data-testid="acc-period-selector">
      {PERIOD_PRESETS.map((p) => (
        <Button key={p.id} size="sm" variant={value === p.id ? "default" : "outline"} className="h-8 text-xs" onClick={() => onChange(p.id)} data-testid={`acc-period-${p.id}`}>
          {p.label}
        </Button>
      ))}
    </div>
  );
}

// --- Filters bar ---
function FiltersBar({ value, onChange, onClear }) {
  const set = (k, v) => onChange({ ...value, [k]: v || undefined });
  return (
    <Card className="p-3 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="acc-orders-filters">
      <Select value={value.delivery_status || "any"} onValueChange={(v) => set("delivery_status", v === "any" ? "" : v)}>
        <SelectTrigger className="h-9" data-testid="acc-filter-delivery"><SelectValue placeholder="Status livrare" /></SelectTrigger>
        <SelectContent><SelectItem value="any">— livrare —</SelectItem>{Object.entries(DELIVERY_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v.t}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={value.invoice_status || "any"} onValueChange={(v) => set("invoice_status", v === "any" ? "" : v)}>
        <SelectTrigger className="h-9" data-testid="acc-filter-invoice"><SelectValue placeholder="Status factură" /></SelectTrigger>
        <SelectContent><SelectItem value="any">— factură —</SelectItem>{Object.entries(INVOICE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v.t}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={value.collection_status || "any"} onValueChange={(v) => set("collection_status", v === "any" ? "" : v)}>
        <SelectTrigger className="h-9" data-testid="acc-filter-collection"><SelectValue placeholder="Status încasare" /></SelectTrigger>
        <SelectContent><SelectItem value="any">— încasare —</SelectItem>{Object.entries(COLLECTION_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v.t}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={value.invoiced || "any"} onValueChange={(v) => set("invoiced", v === "any" ? "" : v)}>
        <SelectTrigger className="h-9" data-testid="acc-filter-invoiced"><SelectValue placeholder="Facturat?" /></SelectTrigger>
        <SelectContent><SelectItem value="any">— oricare —</SelectItem><SelectItem value="yes">Doar facturate</SelectItem><SelectItem value="no">Doar nefacturate</SelectItem></SelectContent>
      </Select>
      <Select value={value.collected || "any"} onValueChange={(v) => set("collected", v === "any" ? "" : v)}>
        <SelectTrigger className="h-9" data-testid="acc-filter-collected"><SelectValue placeholder="Decontat?" /></SelectTrigger>
        <SelectContent><SelectItem value="any">— oricare —</SelectItem><SelectItem value="yes">Decontate</SelectItem><SelectItem value="no">Nedecontate</SelectItem></SelectContent>
      </Select>
      <Input value={value.province || ""} onChange={(e) => set("province", e.target.value)} placeholder="Județ (ex. Cluj)" className="h-9" data-testid="acc-filter-province" />
      <Input value={value.payment_method || ""} onChange={(e) => set("payment_method", e.target.value)} placeholder="Metodă plată (ex. ramburs)" className="h-9" data-testid="acc-filter-payment" />
      <div className="flex items-center gap-2">
        <Input type="number" value={value.amount_min ?? ""} onChange={(e) => set("amount_min", e.target.value)} placeholder="Min RON" className="h-9 w-24" data-testid="acc-filter-min" />
        <Input type="number" value={value.amount_max ?? ""} onChange={(e) => set("amount_max", e.target.value)} placeholder="Max RON" className="h-9 w-24" data-testid="acc-filter-max" />
        <Button variant="ghost" size="sm" onClick={onClear} data-testid="acc-filter-clear"><X className="h-3.5 w-3.5 mr-1" /> Reset</Button>
      </div>
    </Card>
  );
}

function Pagination({ total, skip, limit, onPrev, onNext }) {
  const from = total === 0 ? 0 : skip + 1;
  const to = Math.min(skip + limit, total);
  return (
    <div className="px-4 h-12 flex items-center justify-between text-xs border-t border-border bg-secondary/20">
      <span className="text-muted-foreground">{from.toLocaleString("ro-RO")}–{to.toLocaleString("ro-RO")} din {total.toLocaleString("ro-RO")}</span>
      <div className="flex items-center gap-1">
        <Button variant="ghost" size="sm" disabled={skip === 0} onClick={onPrev} data-testid="acc-pagination-prev"><ChevronLeft className="h-4 w-4" /></Button>
        <Button variant="ghost" size="sm" disabled={to >= total} onClick={onNext} data-testid="acc-pagination-next"><ChevronRight className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}

// --- Order detail (Sheet) ---
function OrderDetailSheet({ orderId, onClose, canMarkPaid: _canMarkPaid }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!orderId) { setData(null); return; }
    setLoading(true);
    getAccountingOrder(orderId).then(setData).catch(() => {}).finally(() => setLoading(false));
  }, [orderId]);

  if (!orderId) return null;
  const open = !!orderId;
  const r = data;

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose?.()}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="acc-order-detail">
        <SheetHeader>
          <SheetTitle className="font-display text-2xl">Comanda #{r?.order_number || "..."}</SheetTitle>
          <SheetDescription>{r?.created_at ? fmtDate(r.created_at) : ""}</SheetDescription>
        </SheetHeader>
        {loading ? <Skeleton className="h-96 w-full mt-4" /> : r && (
          <div className="space-y-4 mt-4 text-sm">
            <Section title="Client">
              <KV k="Nume" v={r.customer_name} />
              <KV k="Email" v={r.customer_email} />
              <KV k="Telefon" v={r.customer_phone} />
              <KV k="Județ" v={r.county} />
              <KV k="Oraș" v={r.city} />
            </Section>
            <Section title="Plată">
              <KV k="Metodă" v={r.payment_method} />
              <KV k="Status financiar" v={r.financial_status} />
              <KV k="Total comandă" v={ron(r.order_total, r.currency)} />
            </Section>
            <Section title="Livrare">
              <KV k="AWB" v={r.awb || "—"} />
              <KV k="Curier" v={r.courier || "—"} />
              <KV k="Status GLS" v={r.delivery_last_event || r.delivery_status_raw || "—"} />
              {r.delivery_status_code && <KV k="Cod GLS" v={r.delivery_status_code} />}
              <KV k="Status Shopify" v={r.fulfillment_status || "—"} />
              <KV k="Status livrare (contabil)" v={DELIVERY_LABEL[r.delivery_status]?.t} />
              <KV k="Livrat la" v={r.delivered_at ? fmtDate(r.delivered_at) : "—"} />
              {r.delivery_last_synced_at && <KV k="Ultim sync GLS" v={fmtRelative(r.delivery_last_synced_at)} />}
              {r.awb_return && <KV k="AWB retur" v={r.awb_return} />}
            </Section>
            <Section title="Factură SmartBill">
              {r.invoice_id ? (
                <>
                  <KV k="Serie / Nr." v={`${r.invoice_series}${r.invoice_number}`} />
                  <KV k="Data" v={r.invoice_date} />
                  <KV k="Valoare" v={ron(r.invoice_total, r.currency)} />
                  <KV k="Status" v={INVOICE_LABEL[r.invoice_status]?.t} />
                  <KV k="Status încasare" v={r.invoice_payment_status} />
                </>
              ) : <p className="text-muted-foreground text-xs">Nicio factură emisă încă pentru această comandă.</p>}
            </Section>
            <Section title="Decont GLS">
              <KV k="Ramburs așteptat" v={ron(r.expected_cod, r.currency)} />
              <KV k="Decontat" v={ron(r.collected_amount, r.currency)} />
              <KV k="Diferență" v={r.amount_diff?.toFixed(2)} />
              <KV k="Data decontării" v={r.remitted_at ? fmtDate(r.remitted_at) : "—"} />
              <KV k="Fișier" v={r.remittance_file || "—"} />
              <KV k="Status" v={COLLECTION_LABEL[r.collection_status]?.t} />
              {r.exception_reason && <KV k="Motiv excepție" v={r.exception_reason} />}
            </Section>
            {r.line_items?.length > 0 && (
              <Section title={`Produse (${r.line_items.length})`}>
                <table className="w-full text-xs">
                  <tbody>
                    {r.line_items.slice(0, 20).map((li, i) => (
                      <tr key={i} className="border-t border-border first:border-0">
                        <td className="py-1 pr-2">{li.title || li.name}</td>
                        <td className="py-1 pr-2 text-muted-foreground">×{li.quantity}</td>
                        <td className="py-1 text-right tabular-nums">{ron(li.price)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Section>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-lg border border-border bg-white p-3 space-y-1">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{title}</div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}
function KV({ k, v }) {
  return (
    <div className="flex items-center justify-between gap-3 text-xs">
      <span className="text-muted-foreground">{k}</span>
      <span className="text-right max-w-[60%] truncate" title={String(v ?? "")}>{v ?? "—"}</span>
    </div>
  );
}

// ============== EXCEPTIONS ==============
function ExceptionsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    setLoading(true);
    listExceptions(300).then((r) => setItems(r.items || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  useRealtimeEvent("accounting.batch_imported", load);

  return (
    <Card className="overflow-x-auto" data-testid="acc-exceptions-card">
      <table className="w-full text-xs">
        <thead className="bg-secondary/40 border-b border-border">
          <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
            <th className="px-3 py-2">AWB</th>
            <th className="px-3 py-2">Order ref</th>
            <th className="px-3 py-2 text-right">Sumă</th>
            <th className="px-3 py-2">Livrat</th>
            <th className="px-3 py-2">Motiv</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {loading ? Array.from({ length: 5 }).map((_, i) => (
            <tr key={i}><td colSpan={5}><Skeleton className="h-4 w-full m-2" /></td></tr>
          )) : items.length === 0 ? (
            <tr><td colSpan={5} className="px-3 py-8 text-center text-muted-foreground">Zero excepții — totul reconciliat. <CheckCircle2 className="h-4 w-4 inline ml-1 text-emerald-600" /></td></tr>
          ) : items.map((it) => (
            <tr key={it.id} data-testid={`acc-exc-row-${it.id}`} className="hover:bg-secondary/30">
              <td className="px-3 py-2 font-mono">{it.awb}</td>
              <td className="px-3 py-2 font-mono">{it.order_ref_raw || "—"}</td>
              <td className="px-3 py-2 text-right tabular-nums">{ron(it.amount, it.currency)}</td>
              <td className="px-3 py-2 text-muted-foreground">{it.delivered_at ? fmtDate(it.delivered_at) : "—"}</td>
              <td className="px-3 py-2 text-muted-foreground">{it.exception_reason || it.reconciliation_status}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

// ============== IMPORTS (GLS upload + SmartBill XLSX) ==============
function ImportsTab({ canImport, canReconcile, canMarkPaid }) {
  return (
    <div className="space-y-8">
      <SmartBillImportSection canImport={canImport} />
      <GlsImportSection canImport={canImport} canReconcile={canReconcile} canMarkPaid={canMarkPaid} />
    </div>
  );
}

function SmartBillImportSection({ canImport }) {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const inputRef = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    listSmartBillImports({ limit: 50 }).then((r) => setBatches(r.items || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  useRealtimeEvent("accounting.smartbill_imported", load);

  const onFile = async (file) => {
    if (!file) return;
    setUploading(true); setProgress(0);
    try {
      const r = await uploadSmartBillXlsx(file, (p) => setProgress(p));
      const newRows = r.inserted + r.updated;
      const linked = r.linked ?? 0;
      const storno = r.storno_total ?? 0;
      const unmatched = r.unmatched_real ?? 0;
      toast.success(
        `SmartBill OK: ${newRows} facturi · ${linked} corelate · ${storno} storno · ${unmatched} necorelate real`
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import SmartBill eșuat");
    } finally {
      setUploading(false); setProgress(0);
      if (inputRef.current) inputRef.current.value = "";
    }
  };
  const onDrop = (e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) onFile(f); };

  const runReclassify = async () => {
    if (!confirm("Reclasific TOATE facturile SmartBill existente (marchez storno pe cele negative, corelez storno → factură originală, recalculez contoarele istoricului). Rulez?")) return;
    try {
      const r = await reclassifyStorno();
      toast.success(
        `Reclasificat OK: ${r.marked_storno} marcate storno · ${r.storno_linked} legate la original · ${r.storno_ambiguous} ambigue · ${r.storno_no_original} fără original`
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reclasificare eșuată");
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-display text-xl font-bold tracking-tight flex items-center gap-2">
          <Receipt className="h-4 w-4 text-primary" /> Import SmartBill
        </h2>
        <p className="text-xs text-muted-foreground mt-1">
          Urcă exportul din SmartBill UI (<em>Rapoarte → Facturi → Export Excel</em>). Hub-ul oglindește facturile READ-ONLY și le corelează automat la comenzile Shopify pe baza câmpului „Observații" (ex. <code className="text-[10px]">Order: #88157</code>).
        </p>
      </div>

      {canImport && (
        <Card
          className="p-8 border-2 border-dashed border-primary/30 bg-primary/5 text-center cursor-pointer hover:border-primary transition"
          onDragOver={(e) => e.preventDefault()} onDrop={onDrop} onClick={() => inputRef.current?.click()}
          data-testid="acc-sb-import-dropzone"
        >
          <input ref={inputRef} type="file" accept=".xlsx,.xls" hidden onChange={(e) => onFile(e.target.files?.[0])} data-testid="acc-sb-import-input" />
          <Receipt className="h-8 w-8 mx-auto text-primary" />
          <div className="font-display font-semibold mt-3">Drag & drop fișier SmartBill XLSX/XLS aici</div>
          <div className="text-xs text-muted-foreground mt-1">Detectare automată antet · auto-match Shopify pe coloana Observații</div>
          {uploading && (
            <div className="mt-4 max-w-sm mx-auto">
              <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                <div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} />
              </div>
              <div className="text-[11px] text-muted-foreground mt-1">{progress}% · se procesează…</div>
            </div>
          )}
        </Card>
      )}

      <Card className="overflow-hidden">
        <div className="px-4 h-12 flex items-center justify-between border-b border-border bg-secondary/40">
          <div className="flex items-center gap-2 text-sm font-semibold"><FileSpreadsheet className="h-4 w-4" /> Istoric importuri SmartBill</div>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={runReclassify}
              data-testid="acc-sb-reclassify-btn"
              className="h-7 text-[11px] gap-1"
              title="Marchează storno-urile deja existente și recalculează contoarele istoricului"
            >
              <Wand2 className="h-3.5 w-3.5" /> Reclasifică storno
            </Button>
            <Button variant="ghost" size="sm" onClick={load} data-testid="acc-sb-batches-refresh"><RefreshCw className="h-3.5 w-3.5" /></Button>
          </div>
        </div>
        <table className="w-full text-xs">
          <thead className="bg-secondary/40 border-b border-border">
            <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
              <th className="px-3 py-2">Fișier</th>
              <th className="px-3 py-2 text-right">Total</th>
              <th className="px-3 py-2 text-right text-emerald-700">Corelate</th>
              <th className="px-3 py-2 text-right text-amber-700">Storno</th>
              <th className="px-3 py-2 text-right text-rose-700">Necorelate real</th>
              <th className="px-3 py-2 text-right">Sumă brută</th>
              <th className="px-3 py-2">Importat</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? Array.from({ length: 3 }).map((_, i) => (
              <tr key={i}><td colSpan={7}><Skeleton className="h-4 w-full m-2" /></td></tr>
            )) : batches.length === 0 ? (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">Niciun import SmartBill încă. Urcă primul export din SmartBill UI.</td></tr>
            ) : batches.map((b) => {
              // Prefer refreshed counters; fall back to legacy `linked` for older batches
              const linkedTotal = b.linked ?? 0;
              const stornoTotal = b.storno_total ?? 0;
              const stornoLinked = b.storno_linked ?? 0;
              const unmatchedReal = b.unmatched_real ?? Math.max(0, (b.inserted ?? 0) + (b.updated ?? 0) - linkedTotal - stornoTotal);
              return (
                <tr key={b.id} data-testid={`acc-sb-batch-${b.id}`} className="hover:bg-secondary/30">
                  <td className="px-3 py-2 font-mono max-w-[220px] truncate" title={b.filename}>{b.filename}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{b.rows_total}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold text-emerald-700" data-testid={`acc-sb-batch-linked-${b.id}`}>
                    {linkedTotal}
                    {stornoLinked > 0 && <span className="text-[10px] text-muted-foreground ml-1">(+{stornoLinked} storno)</span>}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold text-amber-700" data-testid={`acc-sb-batch-storno-${b.id}`}>{stornoTotal}</td>
                  <td className={`px-3 py-2 text-right tabular-nums font-semibold ${unmatchedReal > 0 ? "text-rose-700" : "text-emerald-700"}`} data-testid={`acc-sb-batch-unmatched-${b.id}`}>
                    {unmatchedReal}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{ron(b.total_gross)}</td>
                  <td className="px-3 py-2 text-muted-foreground">{fmtRelative(b.created_at)} · {b.uploaded_by_name || "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function GlsImportSection({ canImport, canReconcile, canMarkPaid }) {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [bulkPayingId, setBulkPayingId] = useState(null);
  const inputRef = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    listImports({ limit: 50 }).then((r) => setBatches(r.items || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const onFile = async (file) => {
    if (!file) return;
    setUploading(true); setProgress(0);
    try {
      const r = await uploadGlsXlsx(file, (p) => setProgress(p));
      toast.success(`GLS OK: ${r.item_count} colete · ${Number(r.total_amount).toLocaleString("ro-RO")} RON. Comenzile primesc update pe status încasare.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import eșuat");
    } finally {
      setUploading(false); setProgress(0);
      if (inputRef.current) inputRef.current.value = "";
    }
  };
  const onDrop = (e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) onFile(f); };

  const remove = async (b) => {
    if (!confirm(`Șterg importul "${b.filename}" cu ${b.item_count} linii?`)) return;
    try { await deleteImport(b.id); toast.success("Șters"); load(); } catch { toast.error("Eroare"); }
  };

  const recompute = async (b) => {
    try { const r = await recomputeImport(b.id); toast.success(`Recalculat (${r.auto_matched} match-uri)`); load(); }
    catch { toast.error("Eroare"); }
  };

  const markAllPaid = async (b) => {
    if (!confirm(`Marchez toate facturile reconciliate din "${b.filename}" ca plătite în SmartBill?`)) return;
    setBulkPayingId(b.id);
    try {
      const r = await markBatchPaid(b.id);
      if (r.failed?.length) toast.error(`${r.pushed} OK · ${r.failed.length} eșuate`);
      else if (r.pushed === 0) toast.message("Nicio factură nou sincronizată");
      else toast.success(`${r.pushed} încasări înregistrate în SmartBill`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare SmartBill"); }
    finally { setBulkPayingId(null); }
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-display text-xl font-bold tracking-tight flex items-center gap-2">
          <Truck className="h-4 w-4 text-amber-600" /> Import GLS
        </h2>
        <p className="text-xs text-muted-foreground mt-1">
          Urcă fișierul XLSX de decont GLS COD. Hub-ul actualizează DOAR statusul de încasare (sumă decontată, dată decont, AWB) pe comenzile existente.
        </p>
      </div>

      {canImport && (
        <Card
          className="p-8 border-2 border-dashed border-border bg-white text-center cursor-pointer hover:border-amber-400 transition"
          onDragOver={(e) => e.preventDefault()} onDrop={onDrop} onClick={() => inputRef.current?.click()}
          data-testid="acc-import-dropzone"
        >
          <input ref={inputRef} type="file" accept=".xlsx,.xls" hidden onChange={(e) => onFile(e.target.files?.[0])} data-testid="acc-import-input" />
          <Upload className="h-8 w-8 mx-auto text-muted-foreground" />
          <div className="font-display font-semibold mt-3">Drag & drop fișier GLS aici</div>
          <div className="text-xs text-muted-foreground mt-1">sau click pentru a alege un .xlsx</div>
          {uploading && (
            <div className="mt-4 max-w-sm mx-auto">
              <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                <div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} />
              </div>
              <div className="text-[11px] text-muted-foreground mt-1">{progress}% · se procesează…</div>
            </div>
          )}
        </Card>
      )}
      <Card className="overflow-hidden">
        <div className="px-4 h-12 flex items-center justify-between border-b border-border bg-secondary/40">
          <div className="flex items-center gap-2 text-sm font-semibold"><FileSpreadsheet className="h-4 w-4" /> Istoric importuri GLS</div>
          <Button variant="ghost" size="sm" onClick={load}><RefreshCw className="h-3.5 w-3.5" /></Button>
        </div>
        <table className="w-full text-xs">
          <thead className="bg-secondary/40 border-b border-border">
            <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
              <th className="px-3 py-2">Fișier</th>
              <th className="px-3 py-2">Cont GLS</th>
              <th className="px-3 py-2 text-right">Colete</th>
              <th className="px-3 py-2 text-right">Sumă</th>
              <th className="px-3 py-2">Data transfer</th>
              <th className="px-3 py-2">Importat</th>
              <th className="px-3 py-2 text-right">Acțiuni</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? Array.from({ length: 3 }).map((_, i) => (
              <tr key={i}><td colSpan={7}><Skeleton className="h-4 w-full m-2" /></td></tr>
            )) : batches.length === 0 ? (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">Niciun import GLS. Trage primul fișier GLS în zona de mai sus.</td></tr>
            ) : batches.map((b) => (
              <tr key={b.id} data-testid={`acc-batch-row-${b.id}`} className="hover:bg-secondary/30">
                <td className="px-3 py-2 font-mono">{b.filename}</td>
                <td className="px-3 py-2 font-mono">{b.client_ref || "—"}</td>
                <td className="px-3 py-2 text-right tabular-nums">{b.item_count}</td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold">{ron(b.total_amount, b.currency)}</td>
                <td className="px-3 py-2 text-muted-foreground">{b.transfer_date ? fmtDate(b.transfer_date) : "—"}</td>
                <td className="px-3 py-2 text-muted-foreground">{fmtRelative(b.created_at)} · {b.uploaded_by_name || "—"}</td>
                <td className="px-3 py-2 text-right">
                  <div className="flex items-center justify-end gap-1">
                    {canMarkPaid && (
                      <Button variant="ghost" size="sm" className="h-7 text-emerald-700" onClick={() => markAllPaid(b)} disabled={bulkPayingId === b.id} data-testid={`acc-batch-mark-paid-${b.id}`}>
                        <BadgeCheck className="h-3.5 w-3.5 mr-1" />{bulkPayingId === b.id ? "..." : "Plătit SB"}
                      </Button>
                    )}
                    {canReconcile && (
                      <Button variant="ghost" size="sm" className="h-7" onClick={() => recompute(b)} data-testid={`acc-batch-recompute-${b.id}`}>
                        <Wand2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {canImport && (
                      <Button variant="ghost" size="sm" className="h-7 hover:text-rose-600" onClick={() => remove(b)} data-testid={`acc-batch-delete-${b.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// ============== EXPORT ==============
function ExportTab({ canExport }) {
  const [period, setPeriod] = useState("last_30d");
  const [filters, setFilters] = useState({});
  const [busy, setBusy] = useState(false);

  const download = async (format) => {
    setBusy(true);
    try {
      const params = { period, ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)) };
      const blob = await downloadExport(format, params);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `contabilitate-${period}-${new Date().toISOString().slice(0, 10)}.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Export ${format.toUpperCase()} descărcat`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare export"); }
    finally { setBusy(false); }
  };

  if (!canExport) return (
    <Card className="p-6 text-center text-sm text-muted-foreground" data-testid="acc-export-forbidden">
      Nu ai permisiunea <code className="font-mono">accounting.export</code>.
    </Card>
  );

  return (
    <div className="space-y-4" data-testid="acc-export-page">
      <PeriodSelector value={period} onChange={setPeriod} />
      <FiltersBar value={filters} onChange={setFilters} onClear={() => setFilters({})} />
      <Card className="p-6 flex flex-col items-center gap-3" data-testid="acc-export-actions">
        <FileText className="h-10 w-10 text-zinc-400" />
        <div className="font-display font-semibold">Exportă comenzile filtrate</div>
        <div className="text-xs text-muted-foreground text-center max-w-md">
          Vei descărca exact ce vezi în tabul Comenzi cu filtrele aplicate. Maxim 5000 rânduri / export.
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => download("xlsx")} disabled={busy} data-testid="acc-export-xlsx">
            <Download className="h-3.5 w-3.5 mr-1" /> XLSX
          </Button>
          <Button variant="outline" onClick={() => download("csv")} disabled={busy} data-testid="acc-export-csv">
            <Download className="h-3.5 w-3.5 mr-1" /> CSV
          </Button>
        </div>
      </Card>
    </div>
  );
}


// ============== NECORELATE (SmartBill invoices without a Shopify link) ==============
const UNMATCHED_REASON_STYLE = {
  storno_no_original:         { cls: "bg-amber-50 text-amber-800 border-amber-200",   label: "Storno · original lipsă" },
  storno_multiple_candidates: { cls: "bg-orange-50 text-orange-800 border-orange-200", label: "Storno · ambiguu" },
  storno_original_unlinked:   { cls: "bg-amber-50 text-amber-800 border-amber-200",   label: "Storno · orig. nelegat" },
  empty_observations:         { cls: "bg-rose-50 text-rose-800 border-rose-200",       label: "Fără Observații" },
  unparseable_observations:   { cls: "bg-rose-50 text-rose-800 border-rose-200",       label: "Obs. neparsabile" },
  order_not_in_hub:           { cls: "bg-rose-50 text-rose-800 border-rose-200",       label: "Comandă absentă" },
  matcher_edge_case:          { cls: "bg-zinc-100 text-zinc-800 border-zinc-200",      label: "Edge case" },
  manual_unlinked:            { cls: "bg-zinc-100 text-zinc-800 border-zinc-200",      label: "Delegat manual" },
};

function UnmatchedTab({ canReconcile, canExport }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [countsByReason, setCountsByReason] = useState({});
  const [reasonLabels, setReasonLabels] = useState({});
  const [loading, setLoading] = useState(true);
  const [reasonFilter, setReasonFilter] = useState("");
  const [includeStorno, setIncludeStorno] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [linkTarget, setLinkTarget] = useState(null); // invoice being linked
  const pageSize = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        limit: pageSize, skip: page * pageSize,
        include_storno: includeStorno,
      };
      if (reasonFilter) params.reason = reasonFilter;
      if (search.trim()) params.search = search.trim();
      const r = await listSmartBillUnmatched(params);
      setItems(r.items || []);
      setTotal(r.total || 0);
      setCountsByReason(r.counts_by_reason || {});
      setReasonLabels(r.reason_labels || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la încărcare necorelate");
    } finally {
      setLoading(false);
    }
  }, [page, reasonFilter, includeStorno, search]);

  useEffect(() => { load(); }, [load]);

  const runReclassify = async () => {
    if (!confirm("Rulez reclasificarea storno pe TOATE facturile? (marchez negativele ca storno + corelez storno → factură originală)")) return;
    try {
      const r = await reclassifyStorno();
      toast.success(
        `Reclasificat: ${r.marked_storno} storno noi · ${r.storno_linked} auto-legate · ${r.storno_ambiguous} ambigue · ${r.storno_no_original} fără original`
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reclasificare eșuată");
    }
  };

  const exportXlsx = async () => {
    try {
      const params = { include_storno: includeStorno };
      if (reasonFilter) params.reason = reasonFilter;
      if (search.trim()) params.search = search.trim();
      const blob = await downloadUnmatchedExport(params);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `smartbill-necorelate-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export XLSX descărcat");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare export");
    }
  };

  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-4" data-testid="acc-unmatched-page">
      <Card className="p-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="font-display text-xl font-bold tracking-tight flex items-center gap-2">
              <Link2 className="h-4 w-4 text-primary" /> Facturi necorelate
            </h2>
            <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
              Facturi SmartBill fără link către o comandă Shopify. Motivul e detectat automat. Storno-urile se leagă automat la factura originală dacă există un singur candidat.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {canReconcile && (
              <Button variant="outline" size="sm" onClick={runReclassify} data-testid="acc-unmatched-reclassify">
                <Wand2 className="h-3.5 w-3.5 mr-1" /> Reclasifică storno
              </Button>
            )}
            {canExport && (
              <Button variant="outline" size="sm" onClick={exportXlsx} data-testid="acc-unmatched-export">
                <Download className="h-3.5 w-3.5 mr-1" /> Export XLSX
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={load} data-testid="acc-unmatched-refresh">
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </Card>

      {/* Reason chips */}
      <Card className="p-3">
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mr-1">Motiv:</span>
          <button
            type="button"
            onClick={() => { setReasonFilter(""); setPage(0); }}
            className={`px-3 py-1 rounded-full text-xs border transition ${reasonFilter === "" ? "bg-primary text-primary-foreground border-primary" : "bg-white hover:bg-secondary/40 border-border"}`}
            data-testid="acc-unmatched-reason-all"
          >
            Toate ({Object.values(countsByReason).reduce((a, b) => a + b, 0)})
          </button>
          {Object.entries(countsByReason).map(([reason, count]) => {
            const style = UNMATCHED_REASON_STYLE[reason] || { cls: "bg-zinc-100 text-zinc-800 border-zinc-200", label: reasonLabels[reason] || reason };
            const active = reasonFilter === reason;
            return (
              <button
                type="button"
                key={reason}
                onClick={() => { setReasonFilter(active ? "" : reason); setPage(0); }}
                className={`px-3 py-1 rounded-full text-xs border transition ${active ? "ring-2 ring-primary ring-offset-1 " : ""}${style.cls}`}
                data-testid={`acc-unmatched-reason-${reason}`}
              >
                {style.label} · {count}
              </button>
            );
          })}
        </div>
      </Card>

      {/* Filters */}
      <Card className="p-3 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 flex-1 min-w-[220px]">
          <Search className="h-3.5 w-3.5 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            placeholder="Caută serie/număr/client/observații…"
            className="h-8 text-xs"
            data-testid="acc-unmatched-search"
          />
        </div>
        <label className="flex items-center gap-1.5 text-xs cursor-pointer select-none" data-testid="acc-unmatched-toggle-storno">
          <input
            type="checkbox"
            checked={includeStorno}
            onChange={(e) => { setIncludeStorno(e.target.checked); setPage(0); }}
            className="rounded border-border"
          />
          Include storno
        </label>
        <div className="text-[11px] text-muted-foreground ml-auto tabular-nums">
          {total} rânduri
        </div>
      </Card>

      {/* Table */}
      <Card className="overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-secondary/40 border-b border-border">
            <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
              <th className="px-3 py-2">Serie/Nr</th>
              <th className="px-3 py-2">Dată</th>
              <th className="px-3 py-2">Client</th>
              <th className="px-3 py-2 text-right">Valoare</th>
              <th className="px-3 py-2">Observații</th>
              <th className="px-3 py-2">Motiv</th>
              <th className="px-3 py-2 text-right">Acțiuni</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? Array.from({ length: 5 }).map((_, i) => (
              <tr key={i}><td colSpan={7}><Skeleton className="h-4 w-full m-2" /></td></tr>
            )) : items.length === 0 ? (
              <tr><td colSpan={7} className="px-3 py-10 text-center text-muted-foreground" data-testid="acc-unmatched-empty">
                <CheckCircle2 className="h-8 w-8 mx-auto text-emerald-500 mb-2" />
                Nicio factură necorelată. Totul este corelat.
              </td></tr>
            ) : items.map((it) => {
              const style = UNMATCHED_REASON_STYLE[it.unmatched_reason] || { cls: "bg-zinc-100 text-zinc-800 border-zinc-200", label: it.reason_label || "Necunoscut" };
              return (
                <tr key={it.id} data-testid={`acc-unmatched-row-${it.id}`} className="hover:bg-secondary/30">
                  <td className="px-3 py-2 font-mono">
                    <div className="font-semibold">{it.series_name}{it.number}</div>
                    {it.is_storno && <Badge variant="outline" className="mt-0.5 text-[9px] bg-amber-50 text-amber-800 border-amber-200">STORNO</Badge>}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">{it.issue_date || "—"}</td>
                  <td className="px-3 py-2">
                    <div className="max-w-[220px] truncate" title={it.customer_name}>{it.customer_name || "—"}</div>
                    {it.customer_vat && <div className="text-[10px] text-muted-foreground font-mono">{it.customer_vat}</div>}
                  </td>
                  <td className={`px-3 py-2 text-right tabular-nums font-semibold ${it.is_storno ? "text-amber-700" : ""}`}>
                    {ron(it.total_gross)}
                  </td>
                  <td className="px-3 py-2 max-w-[280px]">
                    <div className="truncate text-muted-foreground" title={it.observations || ""}>
                      {it.observations || <span className="italic text-zinc-400">(gol)</span>}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <Badge className={`text-[10px] border ${style.cls}`} variant="outline">
                      {style.label}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-right">
                    {canReconcile && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-[11px] gap-1"
                        onClick={() => setLinkTarget(it)}
                        data-testid={`acc-unmatched-link-btn-${it.id}`}
                      >
                        <Link2 className="h-3 w-3" /> Leagă
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {pageCount > 1 && (
          <div className="px-3 py-2 border-t border-border bg-secondary/20 flex items-center justify-between text-xs">
            <div className="text-muted-foreground">Pagina {page + 1} / {pageCount}</div>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)} data-testid="acc-unmatched-prev">
                <ChevronLeft className="h-3 w-3" />
              </Button>
              <Button variant="outline" size="sm" disabled={page + 1 >= pageCount} onClick={() => setPage(page + 1)} data-testid="acc-unmatched-next">
                <ChevronRight className="h-3 w-3" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Manual link drawer */}
      <ManualLinkDrawer
        invoice={linkTarget}
        onClose={() => setLinkTarget(null)}
        onDone={() => { setLinkTarget(null); load(); }}
      />
    </div>
  );
}

function ManualLinkDrawer({ invoice, onClose, onDone }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [linking, setLinking] = useState(false);
  const debounceRef = useRef(null);

  // Pre-populate query with customer name when the drawer opens.
  useEffect(() => {
    if (invoice) {
      setQuery(invoice.customer_name || "");
      setResults([]);
    }
  }, [invoice]);

  useEffect(() => {
    if (!invoice) return;
    if (!query.trim() || query.trim().length < 2) { setResults([]); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const items = await searchShopifyOrders(query.trim(), 30);
        setResults(items);
      } catch (e) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, invoice]);

  const doLink = async (orderId) => {
    if (!invoice) return;
    setLinking(true);
    try {
      await linkInvoiceToOrder(invoice.id, orderId);
      toast.success("Factură legată la comandă");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la legare");
    } finally {
      setLinking(false);
    }
  };

  return (
    <Sheet open={!!invoice} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-[560px] p-0 flex flex-col" data-testid="acc-manual-link-drawer">
        <SheetHeader className="px-5 py-4 border-b border-border">
          <SheetTitle className="font-display text-lg">Leagă factura de comandă</SheetTitle>
          {invoice && (
            <div className="text-xs text-muted-foreground space-y-1" data-testid="acc-manual-link-invoice-summary">
              <div><span className="text-muted-foreground">Factură:</span> <span className="font-mono font-semibold">{invoice.series_name}{invoice.number}</span> · <span className="tabular-nums">{ron(invoice.total_gross)}</span> · {invoice.issue_date}</div>
              <div><span className="text-muted-foreground">Client:</span> {invoice.customer_name || "—"}</div>
              {invoice.observations && <div className="truncate"><span className="text-muted-foreground">Obs:</span> {invoice.observations}</div>}
            </div>
          )}
        </SheetHeader>

        <div className="px-5 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Caută comandă după număr, client, telefon, email…"
              className="h-9 text-sm"
              data-testid="acc-manual-link-search"
            />
          </div>
          <div className="text-[10px] text-muted-foreground mt-2">Minim 2 caractere. Se caută prin toate comenzile Shopify sincronizate în hub.</div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-4 space-y-2">
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : results.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground" data-testid="acc-manual-link-empty">
              {query.trim().length < 2 ? "Începe să scrii pentru a căuta comenzi." : "Nicio comandă găsită."}
            </div>
          ) : (
            <div className="divide-y divide-border">
              {results.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  disabled={linking}
                  onClick={() => doLink(o.id)}
                  className="w-full text-left px-4 py-3 hover:bg-secondary/40 transition disabled:opacity-50 flex items-start justify-between gap-3"
                  data-testid={`acc-manual-link-order-${o.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-mono font-semibold text-sm">{o.order_number || o.name}</div>
                    <div className="text-xs text-muted-foreground truncate">{o.customer_name || "—"}{o.customer_phone ? ` · ${o.customer_phone}` : ""}</div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">{fmtDate(o.created_at)}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="tabular-nums font-semibold text-sm">{ron(o.total)}</div>
                    <Badge variant="outline" className="text-[9px] mt-1">Leagă</Badge>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-border bg-secondary/20 flex justify-end">
          <Button variant="outline" size="sm" onClick={onClose} data-testid="acc-manual-link-cancel">
            <X className="h-3.5 w-3.5 mr-1" /> Închide
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
